/**
 * Tarifs de main d'œuvre et de déplacement, et chiffrage d'une intervention : ce que l'on dit à l'écran (lot U3c, registre B31).
 *
 * Fonctions PURES, sans React. Le serveur reste l'autorité (arithmétique en centimes, résolution site > client > espace,
 * aucune valeur par défaut) ; ici on lit un montant à la française, on décide des phrases, on contrôle la saisie avant l'envoi.
 * Aucun tarif n'est jamais proposé d'avance : « non paramétré » est un état qui se dit, pas un trou que l'on comble.
 */
import type {
  ChampTarifMo,
  ChiffrageInterventionVue,
  EntreeTarifMo,
  PorteeTarifMo,
  TarifMo,
  TarifRetenuVue,
} from '../api/client';
import { dateFrTarif, eurosFr } from './tarifs';

export const CHAMPS: readonly ChampTarifMo[] = ['laborDay', 'laborNight', 'laborWeekend', 'laborHoliday', 'travel'];

const LIBELLE_CHAMP: Readonly<Record<ChampTarifMo, string>> = {
  laborDay: 'Main d’œuvre, jour ouvré',
  laborNight: 'Main d’œuvre, nuit',
  laborWeekend: 'Main d’œuvre, week-end',
  laborHoliday: 'Main d’œuvre, jour férié',
  travel: 'Déplacement',
};

export function libelleChamp(champ: ChampTarifMo): string {
  return LIBELLE_CHAMP[champ];
}

const LIBELLE_PORTEE: Readonly<Record<PorteeTarifMo, string>> = {
  TENANT: 'Toute l’entreprise',
  PARTY: 'Un client',
  SITE: 'Un site',
};

export function libellePortee(portee: PorteeTarifMo): string {
  return LIBELLE_PORTEE[portee];
}

/** La provenance d'un tarif retenu, dite en une expression : « tarif du site », « tarif du client », « tarif de l'espace ». */
const PROVENANCE: Readonly<Record<PorteeTarifMo, string>> = {
  SITE: 'tarif du site',
  PARTY: 'tarif du client',
  TENANT: 'tarif de l’espace',
};

const CATEGORIE: Readonly<Record<'FERIE' | 'WEEKEND' | 'OUVRE', string>> = {
  FERIE: 'jour férié',
  WEEKEND: 'week-end',
  OUVRE: 'jour ouvré',
};

export function libelleCategorie(categorie: 'FERIE' | 'WEEKEND' | 'OUVRE'): string {
  return CATEGORIE[categorie];
}

// ---------------------------------------------------------------------------------------------------------------- saisie

export type MontantSaisi = { readonly ok: true; readonly montant: string | null } | { readonly ok: false; readonly message: string };

/**
 * Un montant tapé à la française : « 75 », « 75,5 », « 75,50 » → « 75.5 » au point décimal. Vide = « hérite » (`null`).
 * Refuse ce qui n'est pas un montant (lettres, trois décimales, négatif) et plus de 100 000 € : jamais un « 0 » deviné.
 */
export function lireMontantFr(texte: string): MontantSaisi {
  const t = texte.replace(/[\s  ]/g, '').replace(',', '.');
  if (t === '') return { ok: true, montant: null };
  if (!/^\d{1,6}(\.\d{1,2})?$/.test(t) || Number(t) > 100_000) {
    return { ok: false, message: 'Un montant en euros, deux décimales au plus (par exemple 75 ou 75,50).' };
  }
  return { ok: true, montant: t };
}

/** « 75.5 » → « 75,50 € » ; `null` → « — ». */
export function montantAffiche(montant: string | null): string {
  if (montant === null) return '—';
  const n = Number(montant);
  return Number.isFinite(n) ? eurosFr(n) : '—';
}

export interface FormulaireTarif {
  readonly portee: PorteeTarifMo;
  readonly partyId: string;
  readonly siteId: string;
  readonly validFrom: string;
  readonly montants: Readonly<Record<ChampTarifMo, string>>;
  readonly note: string;
  /** « Modifier » : le tarif d'origine (son identifiant et sa date d'effet) ; `null` pour un tarif neuf. */
  readonly remplace: { readonly id: string; readonly validFrom: string } | null;
}

export function formulaireVide(aujourdhui: string): FormulaireTarif {
  return {
    portee: 'TENANT',
    partyId: '',
    siteId: '',
    validFrom: aujourdhui,
    montants: { laborDay: '', laborNight: '', laborWeekend: '', laborHoliday: '', travel: '' },
    note: '',
    remplace: null,
  };
}

/** « 75.00 » → « 75,00 » : un montant enregistré, prêt à être retouché à la française ; `null` → case vide (« hérite »). */
function montantPourSaisie(montant: string | null): string {
  return montant === null ? '' : montant.replace('.', ',');
}

/** « Modifier » : le formulaire prérempli d'après un tarif existant — même portée, même cible, mêmes montants, même date. */
export function formulaireDepuisTarif(t: TarifMo): FormulaireTarif {
  return {
    portee: t.portee,
    partyId: t.partyId ?? '',
    siteId: t.siteId ?? '',
    validFrom: t.validFrom,
    montants: {
      laborDay: montantPourSaisie(t.laborDay),
      laborNight: montantPourSaisie(t.laborNight),
      laborWeekend: montantPourSaisie(t.laborWeekend),
      laborHoliday: montantPourSaisie(t.laborHoliday),
      travel: montantPourSaisie(t.travel),
    },
    note: t.note ?? '',
    remplace: { id: t.id, validFrom: t.validFrom },
  };
}

export type EffetModification =
  | { readonly genre: 'CORRECTION'; readonly phrase: string }
  | { readonly genre: 'CHANGEMENT'; readonly phrase: string }
  | { readonly genre: 'INVALIDE'; readonly phrase: string };

/**
 * Ce que « Modifier » va faire, dit AVANT d'enregistrer : même date d'effet = correction (l'ancien est archivé) ; date plus
 * tardive = changement de prix (l'ancien reste valable jusqu'à la veille) ; date antérieure = refusée (le serveur la refuse aussi).
 */
export function effetModification(dateOrigine: string, nouvelleDate: string): EffetModification {
  if (nouvelleDate === dateOrigine) {
    return { genre: 'CORRECTION', phrase: 'Correction : ce tarif remplacera l’ancien, qui sera archivé (comme s’il n’avait jamais existé).' };
  }
  if (nouvelleDate > dateOrigine) {
    return {
      genre: 'CHANGEMENT',
      phrase: `Changement de prix à partir du ${dateFrTarif(nouvelleDate)} : l’ancien tarif reste valable jusqu’à la veille, il n’est pas archivé.`,
    };
  }
  return {
    genre: 'INVALIDE',
    phrase: `La date d’effet ne peut pas précéder celle du tarif modifié (${dateFrTarif(dateOrigine)}) : pour une période antérieure, définis un nouveau tarif.`,
  };
}

export type EntreeValidee = { readonly ok: true; readonly entree: EntreeTarifMo } | { readonly ok: false; readonly message: string };

/** Contrôles avant l'envoi : une cible quand la portée en exige une, une date, au moins un montant lisible. */
export function validerFormulaire(f: FormulaireTarif): EntreeValidee {
  if (f.portee === 'PARTY' && f.partyId === '') return { ok: false, message: 'Choisissez le client.' };
  if (f.portee === 'SITE' && f.siteId === '') return { ok: false, message: 'Choisissez le site.' };
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.validFrom)) return { ok: false, message: 'La date d’effet est obligatoire.' };
  if (f.remplace !== null) {
    const effet = effetModification(f.remplace.validFrom, f.validFrom);
    if (effet.genre === 'INVALIDE') return { ok: false, message: effet.phrase };
  }
  const montants: Partial<Record<ChampTarifMo, string | null>> = {};
  let renseignes = 0;
  for (const champ of CHAMPS) {
    const lu = lireMontantFr(f.montants[champ]);
    if (!lu.ok) return { ok: false, message: `${libelleChamp(champ)} : ${lu.message}` };
    montants[champ] = lu.montant;
    if (lu.montant !== null) renseignes += 1;
  }
  if (renseignes === 0) return { ok: false, message: 'Renseignez au moins un montant : un tarif vide ne dirait rien.' };
  return {
    ok: true,
    entree: {
      portee: f.portee,
      partyId: f.portee === 'PARTY' ? f.partyId : null,
      siteId: f.portee === 'SITE' ? f.siteId : null,
      montants,
      validFrom: f.validFrom,
      note: f.note.trim() === '' ? null : f.note.trim(),
      ...(f.remplace !== null ? { remplace: f.remplace.id } : {}),
    },
  };
}

// ----------------------------------------------------------------------------------------------------------- tarif retenu

/**
 * Ce qui s'applique à un site tel jour : « 75,00 € (tarif du client, depuis le 01/01/2026) » ou « non paramétré ».
 * La nuit n'est pas déduite (aucune heure de début connue) : la phrase d'ensemble le dit.
 */
export function phraseTarifRetenu(t: TarifRetenuVue): string {
  if (t.montant === null || t.portee === null) return 'non paramétré';
  const depuis = t.validFrom === null ? '' : `, depuis le ${dateFrTarif(t.validFrom)}`;
  return `${montantAffiche(t.montant)} (${PROVENANCE[t.portee]}${depuis})`;
}

export const PHRASE_NUIT =
  'La nuit n’est pas déduite : sans heure de début connue, le tarif de nuit ne s’applique jamais tout seul.';

// ----------------------------------------------------------------------------------------------------------------- chiffrage

const RAISON: Readonly<Record<string, string>> = {
  TARIF_NON_PARAMETRE: 'Tarif non paramétré',
  SANS_HEURES: 'Aucune heure renseignée',
  SANS_MACHINE: 'Intervention sans machine',
  SANS_MARQUE: 'La machine n’a pas de marque',
  SANS_REFERENCE: 'Pièce sans référence',
  HORS_TARIF: 'Référence absente du tarif',
  REFERENCE_AMBIGUE: 'Référence présente plusieurs fois au tarif',
  QUANTITE_INCONNUE: 'Quantité inconnue',
  SAISIE_A_LA_MAIN: 'Ligne saisie à la main : non chiffrée ici',
  PORTE_PAR_LA_PORTEUSE: 'Compté une seule fois, sur l’intervention porteuse du passage',
  AUCUNE_CONDITION: 'Aucune condition d’achat pour cette référence',
  FAMILLE_INCONNUE: 'Famille du tarif inconnue : posez une exception pour cette référence',
  PRIX_TARIF_INUTILISABLE: 'Prix du tarif inutilisable',
  ACHAT_NUL: 'Prix d’achat nul',
  MONTANT_INVRAISEMBLABLE: 'Montant invraisemblable',
  SANS_PRIX_DU_TARIF: 'Pas de prix au tarif',
  SANS_PRIX_EQUIVALENT: 'Équivalent sans prix : coût réel inconnu',
  CONDITION_ILLISIBLE: 'Condition illisible',
};

export function phraseRaison(raison: string | null): string {
  if (raison === null) return '';
  return RAISON[raison] ?? 'Non chiffré';
}

/** Vrai quand le geste utile est de paramétrer un tarif de main d'œuvre ou de déplacement (le lien « Voulez-vous paramétrer ? »). */
export function raisonSeParametre(raison: string | null): boolean {
  return raison === 'TARIF_NON_PARAMETRE';
}

/**
 * D'où vient ce chiffrage : figé à la validation (il ne bouge plus), calculé d'après les tarifs d'aujourd'hui (pas encore validée),
 * ou calculé alors que l'intervention est validée (validée avant que le chiffrage ne soit figé : rien n'a été conservé).
 */
export function phraseOrigineChiffrage(c: ChiffrageInterventionVue): string {
  if (c.fige) return 'Chiffrage figé à la validation : il ne change plus, même si les tarifs changent ensuite.';
  if (c.statut === 'CANCELLED') return 'Intervention annulée : elle ne se fait pas, rien n’est chiffré.';
  if (c.statut === 'REVIEWED') {
    return 'Cette intervention a été validée avant que le chiffrage ne soit conservé : il est recalculé d’après les tarifs d’aujourd’hui.';
  }
  return 'Chiffrage d’après les tarifs d’aujourd’hui : il sera figé à la validation de l’intervention.';
}

export interface ResumeChiffrage {
  /** Le total À FACTURER, à la française. */
  readonly total: string;
  readonly coutAchat: string | null;
  /** « À facturer : 131,25 € HT » (ou « au moins … » avec ce qui manque). */
  readonly phraseTotal: string;
  /**
   * « Compris au contrat annuel : 245,00 € HT (non facturé) » : la visite d'un contrat annuel, chiffrée pour la rentabilité.
   * `null` quand rien n'est compris et que le bon n'est pas sous contrat annuel.
   */
  readonly phraseCompris: string | null;
  /** Le total À FACTURER est un minimum : au moins une ligne à facturer qui devrait être chiffrée ne l'est pas. Les lignes comprises n'y comptent pas. */
  readonly incomplet: boolean;
  readonly aParametrer: boolean;
}

const pluriel = (n: number, mot: string) => `${n} ${mot}${n > 1 ? 's' : ''}`;

/**
 * Les phrases du bas du chiffrage (règle de Benjamin, 2026-10-24) : le total À FACTURER, et ce qui manque pour qu'il soit complet ;
 * puis, sous contrat annuel ou dès qu'une ligne est comprise, le total COMPRIS au contrat (non facturé). Une ligne comprise non
 * chiffrée ne rend jamais le total à facturer incomplet (le serveur ne la compte pas) : elle fait seulement du compris un minimum.
 */
export function resumerChiffrage(c: ChiffrageInterventionVue): ResumeChiffrage {
  const aParametrer = c.lignes.some((l) => l.compte && l.total === null && raisonSeParametre(l.raison));
  const total = montantAffiche(c.totalHt);
  let phraseTotal: string;
  if (c.statut === 'CANCELLED') phraseTotal = 'Annulée : rien à facturer';
  else if (!c.incomplet) phraseTotal = `À facturer : ${total} HT`;
  else phraseTotal = `À facturer : au moins ${total} HT — ${pluriel(c.nonChiffrees, 'ligne')} non chiffrée${c.nonChiffrees > 1 ? 's' : ''}`;

  const compris = Number(c.totalComprisHt);
  let phraseCompris: string | null = null;
  if ((Number.isFinite(compris) && compris !== 0) || c.facturationBon === 'CONTRACT') {
    const manquantes = c.lignes.filter((l) => l.facturation === 'COMPRIS' && l.compte && l.total === null).length;
    const montant = montantAffiche(c.totalComprisHt);
    phraseCompris =
      manquantes === 0
        ? `Compris au contrat annuel : ${montant} HT (non facturé)`
        : `Compris au contrat annuel : au moins ${montant} HT (non facturé) — ${pluriel(manquantes, 'ligne')} non chiffrée${manquantes > 1 ? 's' : ''}`;
  }
  const achat = Number(c.coutAchatPieces);
  return {
    total,
    coutAchat: Number.isFinite(achat) && achat > 0 ? montantAffiche(c.coutAchatPieces) : null,
    phraseTotal,
    phraseCompris,
    incomplet: c.incomplet,
    aParametrer,
  };
}
